import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card, Button, Alert } from "react-bootstrap";
import { Navbar } from "@avlino-inc/alenza-react";
// import "react-phone-input-2/dist/style.css";
import ClipLoader from "react-spinners/ClipLoader";
import { override } from "components/spinner/spinnerOverride";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import queryString from "query-string";
import {
  verifyAuthyOtpAction,
  textAuthyOtpAction,
  callAuthyOtpAction,
  logoutUserAction,
  setGlobalMessageAction,
} from "store/actions/actionCreators";
import {
  getAuthyVerifyReducerResponse,
  getAuthyVerifyReducerError,
  getAuthyOtpReducerResponse,
  getAuthyOtpReducerError,
  getGlobalMessageReducerResponse,
} from "store/selectors";
import { getTheme } from "lib/theme";
import genStyles from "styles/General.module.scss";
import styles from "./Auth.module.scss";
import { ThemeLogo } from "components/pages/auth/ThemeLogo";
import {
  SuccessType,
  AuthSuccessType,
  ErrorType,
  VerifyAuthyOtpRequestType,
} from "types";
import { handleGlobalMessageAlert } from "util/reactSnippets";
import * as routes from "routes";

type VerifyAuthyOtpType = {
  value: string | undefined;
};

type PossibleUrlParams = {
  redirect?: string;
};

const AuthTwoFactorVerificationPage = ({
  authed,
  tfaRegistered,
  tfaVerified,
}: {
  authed: Promise<boolean>;
  tfaRegistered: Promise<boolean>;
  tfaVerified: Promise<boolean>;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const authyVerifyResponse: AuthSuccessType = useSelector((state) =>
    getAuthyVerifyReducerResponse(state, "data")
  );
  const authyVerifyError: ErrorType = useSelector((state) =>
    getAuthyVerifyReducerError(state)
  );
  const authyOtpResponse: SuccessType = useSelector((state) =>
    getAuthyOtpReducerResponse(state, "data")
  );
  const authyOtpError: ErrorType = useSelector((state) =>
    getAuthyOtpReducerError(state)
  );
  const globalMessage: string | undefined = useSelector((state) =>
    getGlobalMessageReducerResponse(state)
  );

  const OTP_LENGTH = 6;
  const [authorized, setAuthorized] = useState<boolean>(false);
  const [otp, setOtp] = useState<VerifyAuthyOtpType>();
  const [isVerifying, setIsVerifying] = useState<boolean>();
  const [isUpdatingOtp, setIsUpdatingOtp] = useState<boolean>(false);
  const [displayMessage, setDisplayMessage] = useState<string>(
    "Enter the verification code shown in your Authy app"
  );
  const [theme, setTheme] = useState<string | undefined>();
  const [urlParams] = useState<PossibleUrlParams>(
    queryString.parse(location.search)
  );

  useEffect(() => {
    if (authed && tfaRegistered) {
      authed.then((allow) => {
        if (allow) {
          tfaVerified.then((isAlreadyVerified) => {
            if (isAlreadyVerified) {
              theme === "aicon"
                ? history.push(routes.AICON_DUMMY_ROUTE)
                : history.push(routes.DASHBOARD_MAIN);
            } else {
              tfaRegistered.then((isRegistered) => {
                if (isRegistered) {
                  setAuthorized(true);
                  !theme
                    ? setTheme(getTheme())
                    : log.info("Theme already set.");
                } else {
                  history.push(routes.TWOFACTOR_REGISTER);
                }
              });
            }
          });
        } else {
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, tfaRegistered, tfaVerified, history, theme]);

  useEffect(() => {
    if (authyVerifyResponse && authyVerifyResponse.token) {
      localStorage.setItem("user_session", authyVerifyResponse.token);
      theme === "aicon"
        ? history.push(routes.AICON_DUMMY_ROUTE)
        : history.push(routes.DASHBOARD_MAIN);
      dispatch(setGlobalMessageAction({ message: undefined }));
    }
  }, [authyVerifyResponse, history]);

  useEffect(() => {
    // Google Analytics - On Page Load
    setIsVerifying(false);
  }, [authyVerifyError]);

    /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const onHandleLogout = (event: any) => {
    event.preventDefault();
    dispatch(logoutUserAction({}));
    history.push(routes.LOGIN);
  };

  const onHandleTextCodeClick = (event: any) => {
    event.preventDefault();
    setDisplayMessage("Enter the verification code sent to your phone");
    dispatch(textAuthyOtpAction({}));
  };

  const onHandleCallCodeClick = (event: any) => {
    event.preventDefault();
    setDisplayMessage("Enter the verification code we called your phone with");
    dispatch(callAuthyOtpAction({}));
  };

  const onHandleAuthyVerifyClick = (event: any) => {
    event.preventDefault();
    if (otp && otp?.value) {
      setIsVerifying(true);
      log.info("All fields are valid! Verifying OTP ...");
      const request: VerifyAuthyOtpRequestType = {
        body: {
          otp: otp.value,
        },
      };
      log.info("-- Dispatching registerAuthyUserAction()...");
      dispatch(verifyAuthyOtpAction(request));
    } else {
      log.info(
        "One or more fields NOT VALID! Will not register two-factor. " +
          JSON.stringify(otp)
      );
    }
  };

  const badVerifyAlert = (data: SuccessType, error: ErrorType) => {
    if (data && data.success) {
      log.info("Successful Authy Verification!");
    } else if (error) {
      log.info("Unsuccessful Login.");
      return <Alert variant="danger">Unable to verify device</Alert>;
    }
    return null;
  };

  const isOtpValid = (otp: VerifyAuthyOtpType | undefined) => {
    if (otp && otp.value && otp.value.length === OTP_LENGTH) {
      return true;
    }
    return false;
  };

  const onClickUserCard = useCallback(() => history.push(routes.LOGIN), []);

  return (
    <div id="AuthTwoFactorVerificationPage" className={styles["auth-page"]}>
      <Navbar
        // rightImage={alenza_logo}
        logoUrl="https://avlino.com"
        showLogo={false}
        productNameUrl="/"
        email={""}
        onClickUserCard={onClickUserCard}
        dropdown={undefined}
      />
      {handleGlobalMessageAlert(globalMessage, true)}
      <Container className={styles["auth-page__container"]} fluid={true}>
        <Row className={styles["auth-page__container__row"]}>
          <Col md={12} className={styles["auth-page__container__row__content"]}>
            <Card
              className={
                styles["auth-page__container__row__content__auth-card"]
              }
            >
              <Card.Body className="text-center">
                <div>
                  <ThemeLogo theme={theme} />
                </div>
                <Card.Title>Two-factor Verification</Card.Title>
                {!isUpdatingOtp
                  ? badVerifyAlert(authyOtpResponse, authyOtpError)
                  : null}
                <form>
                  <div>
                    <input
                      type="text"
                      name="otp"
                      placeholder={`${OTP_LENGTH}-digit code`}
                      maxLength={OTP_LENGTH}
                      value={otp?.value}
                      onChange={(e) => setOtp({ value: e.target.value })}
                      autoFocus
                      className={genStyles["alenza__input"]}
                    />
                  </div>
                  <Button
                    type="submit"
                    className={genStyles["alenza__btn-primary"]}
                    center-block="true"
                    disabled={isVerifying || !isOtpValid(otp)}
                    onClick={(e: any) => {
                      onHandleAuthyVerifyClick(e);
                    }}
                  >
                    {isVerifying ? (
                      <ClipLoader
                        color={"#ffffff"}
                        css={override}
                        size={14}
                        loading={isVerifying}
                      />
                    ) : (
                      <>Verify</>
                    )}
                  </Button>
                </form>
                <Container>
                  <Row>
                    <Col md={12}>
                      <button
                        type="submit"
                        value="Text Me"
                        className={genStyles["alenza__btn-sq-yellow"]}
                        onClick={onHandleTextCodeClick}
                      >
                        <FontAwesomeIcon icon={["fal", "mobile"]} />
                        <p style={{ fontSize: "12px" }}>Text Me</p>
                      </button>
                      <button
                        type="submit"
                        value="Call Me"
                        className={genStyles["alenza__btn-sq-blue"]}
                        onClick={onHandleCallCodeClick}
                      >
                        <FontAwesomeIcon icon={["fal", "phone-volume"]} />
                        <p style={{ fontSize: "12px" }}>Call Me</p>
                      </button>
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
              <Card.Footer>
                <div style={{ marginTop: "10px" }}>
                  <div>
                    <p>
                      Don't have the Authy app?{" "}
                      <a
                        href="https://authy.com/download/"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Download Authy
                      </a>
                    </p>
                  </div>
                  <div
                    className={genStyles["link"]}
                    onClick={() => history.push(routes.FORGOTTEN_PHONE)}
                  >
                    Reset Phone Number
                  </div>
                </div>
                <div>
                  <div className={genStyles["link"]} onClick={onHandleLogout}>
                    Sign in as another user
                  </div>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthTwoFactorVerificationPage;
